import React, { useState, useEffect, useCallback, useRef } from "react";
import { MapContainer, TileLayer, Marker, Popup, Polyline, useMap, useMapEvents } from "react-leaflet";
import L from "leaflet";
import { routePlannerAPI, logisticsAPI, formatError } from "../utils/api";
import { useKitchen } from "../contexts/KitchenContext";
import { useAuth } from "../contexts/AuthContext";
import { fmtDateTime } from "../utils/dateUtils";
import { MapLayersControl, LayerPreferenceSaver, ClickAndDragMap, getLayerPref } from "../utils/mapUtils";
import {
  Map, Plus, X, Search, RefreshCw, Loader, CheckCircle,
  AlertTriangle, Navigation, ArrowRight, BarChart3, Clock,
  GripVertical, Trash2, Edit, Eye, EyeOff, Download,
  MapPin, Crosshair, Link, Type, Undo2
} from "lucide-react";
import {
  DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors,
} from "@dnd-kit/core";
import {
  SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy,
  useSortable, arrayMove,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

// Fix Leaflet default marker icons
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png",
  iconUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png",
  shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png",
});

const ROUTE_COLORS = [
  "#E8956D","#637C64","#4A90E2","#E74C3C","#9B59B6",
  "#27AE60","#F39C12","#16A085","#D35400","#2C3E50",
];

function numberedIcon(num, color) {
  return L.divIcon({
    className: "",
    html: `<div style="background:${color};color:#fff;border:2px solid #fff;border-radius:50%;width:28px;height:28px;display:flex;align-items:center;justify-content:center;font-weight:700;font-size:12px;box-shadow:0 2px 6px rgba(0,0,0,0.4)">${num}</div>`,
    iconSize: [28, 28],
    iconAnchor: [14, 14],
  });
}

function FlyTo({ center }) {
  const map = useMap();
  useEffect(() => { if (center) map.flyTo(center, 12, { duration: 1 }); }, [center]);
  return null;
}

// ─── Sortable stop item ───────────────────────────────────────────────────────
function SortableStop({ stop, color, onRemove, onFocus }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: stop.school_id });
  return (
    <div ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1 }}
      className="flex items-center gap-2 bg-white border border-[#E5E1DB] rounded-lg px-2 py-2 text-xs group"
    >
      <div {...attributes} {...listeners} className="cursor-grab text-[#C4BAAF] hover:text-[#8A7E73]">
        <GripVertical size={13} />
      </div>
      <span className="w-5 h-5 rounded-full flex items-center justify-center text-white font-bold text-[10px] shrink-0"
        style={{ background: color }}>{stop.sequence}</span>
      <span className="flex-1 font-medium text-[#3A322C] truncate">{stop.school_name}</span>
      {stop.mandal && <span className="text-[#C4BAAF] hidden group-hover:inline">{stop.mandal}</span>}
      <button onClick={() => onFocus([stop.lat, stop.lng])} disabled={!stop.lat}
        className="text-[#8A7E73] hover:text-[#E8956D] disabled:opacity-30" title="Focus on map"><MapPin size={12} /></button>
      <button onClick={() => onRemove(stop.school_id)} className="text-[#8A7E73] hover:text-[#C2624E]"><X size={12} /></button>
    </div>
  );
}

const TABS = ["planner","geocode","history"];

export default function RoutePlanner() {
  const { selectedKitchen } = useKitchen();
  const { user } = useAuth();
  const canEdit = user?.is_admin || !!user?.permissions?.route_planner;

  const [tab, setTab] = useState("planner");
  const [routes, setRoutes] = useState([]);
  const [selectedRouteId, setSelectedRouteId] = useState(null);
  const [visibleRouteIds, setVisibleRouteIds] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState("");
  const [error, setError] = useState("");
  const [mapCenter, setMapCenter] = useState(null);

  // ── View as of date (historical snapshot) ──
  const [asOfDate, setAsOfDate] = useState("");        // "" = current/live
  const [asOfRoutes, setAsOfRoutes] = useState(null);  // null = use live routes
  const [asOfLoading, setAsOfLoading] = useState(false);

  const loadAsOf = async (dateStr) => {
    if (!dateStr) { setAsOfRoutes(null); return; }
    setAsOfLoading(true);
    try {
      const r = await routePlannerAPI.getRoutesAsOf({ as_of: dateStr });
      setAsOfRoutes(r.data || []);
    } catch (e) { if (process.env.NODE_ENV === 'development') console.error('Fetch error:', e); }
    setAsOfLoading(false);
  };

  // The routes shown on the map: historical snapshot or live
  const displayRoutes = asOfRoutes !== null ? asOfRoutes : routes;

  // School search
  const [schoolSearch, setSchoolSearch] = useState("");
  const [schoolResults, setSchoolResults] = useState([]);
  const [unassigned, setUnassigned] = useState([]);
  const [unassignedSearch, setUnassignedSearch] = useState("");

  // Route create
  const [showCreate, setShowCreate] = useState(false);
  const [createForm, setCreateForm] = useState({ route_number: "", name: "", kitchen_id: selectedKitchen || "" });

  // Stops state (editable, for drag-sort)
  const [editingStops, setEditingStops] = useState([]);
  const stopsDirty = useRef(false);

  // Move schools
  const [selectedSchoolIds, setSelectedSchoolIds] = useState(new Set());
  const [moveToRouteId, setMoveToRouteId] = useState("");

  // Migration
  const [migrating, setMigrating] = useState(false);

  // Geocode
  const [geoStatus, setGeoStatus] = useState(null);
  const [geoLoading, setGeoLoading] = useState(false);
  const [pinDropSchool, setPinDropSchool] = useState(null);
  const [pinPos, setPinPos] = useState(null);

  // History
  const [changelog, setChangelog] = useState([]);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const fetchRoutes = useCallback(async () => {
    setLoading(true);
    try {
      const r = await routePlannerAPI.getRoutes({ include_retired: false, kitchen_id: selectedKitchen || undefined });
      setRoutes(r.data || []);
      if ((r.data || []).length > 0 && !selectedRouteId) {
        setSelectedRouteId(r.data[0].id);
      }
    } catch (e) { /* silenced */ }
    setLoading(false);
  }, [selectedKitchen, selectedRouteId]);

  useEffect(() => { fetchRoutes(); }, [selectedKitchen]);
  useEffect(() => {
    if (tab === "history") loadChangelog();
    if (tab === "geocode") loadGeoStatus();
  }, [tab]);

  const loadChangelog = async () => {
    try {
      const r = await routePlannerAPI.getChangeLog({ limit: 100 });
      setChangelog(r.data || []);
    } catch (e) { /* silenced */ }
  };

  const loadGeoStatus = async () => {
    try {
      const r = await routePlannerAPI.geocodeStatus();
      setGeoStatus(r.data);
    } catch (e) { /* silenced */ }
  };

  const selectedRoute = routes.find(r => r.id === selectedRouteId);
  const color = (routeId) => ROUTE_COLORS[routes.findIndex(r => r.id === routeId) % ROUTE_COLORS.length];

  useEffect(() => {
    if (selectedRoute) {
      const stops = selectedRoute.stops || [];
      setEditingStops([...stops]);
      stopsDirty.current = false;
      // Show this route on map
      setVisibleRouteIds(prev => new Set([...prev, selectedRoute.id]));
    }
  }, [selectedRouteId, selectedRoute?.stops?.length]);

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (active.id !== over?.id) {
      setEditingStops(items => {
        const oldIdx = items.findIndex(i => i.school_id === active.id);
        const newIdx = items.findIndex(i => i.school_id === over.id);
        const reordered = arrayMove(items, oldIdx, newIdx).map((s, i) => ({ ...s, sequence: i + 1 }));
        stopsDirty.current = true;
        return reordered;
      });
    }
  };

  const saveOrder = async () => {
    if (!selectedRouteId || !stopsDirty.current) return;
    try {
      await routePlannerAPI.reorderStops(selectedRouteId, editingStops.map(s => ({ school_id: s.school_id, sequence: s.sequence })));
      setMsg("Stop order saved!");
      stopsDirty.current = false;
      fetchRoutes();
      setTimeout(() => setMsg(""), 2000);
    } catch (e) { setError(formatError(e)); }
  };

  const searchSchools = async (q) => {
    setSchoolSearch(q);
    if (q.length < 2) { setSchoolResults([]); return; }
    try {
      const r = await routePlannerAPI.getSchools({ search: q, limit: 20 });
      setSchoolResults((r.data?.schools || []).filter(s => !editingStops.some(st => st.school_id === s.id)));
    } catch (e) { /* silenced */ }
  };

  const addSchool = async (school) => {
    if (!selectedRouteId) return;
    try {
      await routePlannerAPI.addSchool(selectedRouteId, { school_id: school.id });
      setMsg(`${school.school_name} added!`);
      setSchoolSearch(""); setSchoolResults([]);
      fetchRoutes();
      setTimeout(() => setMsg(""), 2000);
    } catch (e) { setError(formatError(e)); }
  };

  const removeSchool = async (schoolId) => {
    if (!selectedRouteId) return;
    try {
      await routePlannerAPI.removeSchool(selectedRouteId, schoolId);
      fetchRoutes();
    } catch (e) { setError(formatError(e)); }
  };

  const handleCreate = async () => {
    try {
      await routePlannerAPI.createRoute({ ...createForm, kitchen_id: selectedKitchen || "" });
      setShowCreate(false);
      setCreateForm({ route_number: "", name: "", kitchen_id: "" });
      fetchRoutes();
    } catch (e) { setError(formatError(e)); }
  };

  const retireRoute = async (id) => {
    if (!window.confirm("Retire this route? Drivers won't see it but historical data is preserved.")) return;
    try {
      await routePlannerAPI.retireRoute(id);
      fetchRoutes();
    } catch (e) { setError(formatError(e)); }
  };

  const moveSelectedSchools = async () => {
    if (!selectedRouteId || !moveToRouteId || selectedSchoolIds.size === 0) return;
    const from = routes.find(r => r.id === selectedRouteId);
    const to = routes.find(r => r.id === moveToRouteId);
    const names = [...selectedSchoolIds].map(id => editingStops.find(s => s.school_id === id)?.school_name).filter(Boolean);
    if (!window.confirm(`Move ${names.length} school(s) from Route ${from?.route_number} to Route ${to?.route_number}?\n\n${names.slice(0,5).join(", ")}${names.length > 5 ? "..." : ""}`)) return;
    try {
      const r = await routePlannerAPI.moveSchools({ school_ids: [...selectedSchoolIds], from_route_id: selectedRouteId, to_route_id: moveToRouteId });
      setMsg(r.data?.message || "Schools moved!");
      setSelectedSchoolIds(new Set());
      setMoveToRouteId("");
      fetchRoutes();
      setTimeout(() => setMsg(""), 3000);
    } catch (e) { setError(formatError(e)); }
  };

  const loadUnassigned = async () => {
    try {
      const r = await routePlannerAPI.getUnassigned({ search: unassignedSearch || undefined });
      setUnassigned(r.data?.schools || []);
    } catch (e) { if (process.env.NODE_ENV === 'development') console.error('Fetch error:', e); }
  };
  useEffect(() => { if (tab === "planner") loadUnassigned(); }, [unassignedSearch, tab]); // eslint-disable-line react-hooks/exhaustive-deps

  const runMigration = async () => {
    if (!window.confirm("Sync all routes from existing school assignments?\n\nThis will populate each route's stop list from the school master data. Routes already with stops will be skipped.")) return;
    setMigrating(true);
    try {
      const r = await routePlannerAPI.migrateStops();
      setMsg(r.data?.message || "Migration complete!");
      fetchRoutes();
      setTimeout(() => setMsg(""), 6000);
    } catch (e) { setError(formatError(e)); }
    setMigrating(false);
  };

  const startGeocodeAll = async () => {
    setGeoLoading(true);
    try {
      const r = await routePlannerAPI.geocodeAll();
      setMsg(r.data?.message || "Geocoding started in background. Refresh status to track progress.");
      setTimeout(() => setMsg(""), 5000);
    } catch (e) { setError(formatError(e)); }
    setGeoLoading(false);
  };

  const captureLocation = (schoolId) => {
    if (!navigator.geolocation) {
      setError("Geolocation not supported by this browser.");
      return;
    }
    if (location.protocol !== "https:" && location.hostname !== "localhost") {
      setError("GPS capture requires HTTPS. Use the manual coordinate entry or pin-drop instead.");
      return;
    }
    navigator.geolocation.getCurrentPosition(async (pos) => {
      try {
        await routePlannerAPI.setSchoolLocation(schoolId, {
          lat: pos.coords.latitude, lng: pos.coords.longitude, location_status: "verified"
        });
        setMsg("GPS location captured and saved!");
        loadGeoStatus();
        setTimeout(() => setMsg(""), 3000);
      } catch (e) { setError(formatError(e)); }
    }, (err) => {
      const msgs = {
        1: "Location permission denied. Please allow location access in your browser settings.",
        2: "Unable to determine location. Try the manual entry or pin-drop instead.",
        3: "Location request timed out. Try again.",
      };
      setError(msgs[err.code] || "Unable to get location.");
    }, { enableHighAccuracy: true, timeout: 10000 });
  };

  // Determine map routes to show (historical or live)
  const mapRoutes = displayRoutes.filter(r => visibleRouteIds.has(r.id) || (asOfRoutes !== null));

  // ── Polyline positions per visible route ──
  const routePolylines = mapRoutes.map(r => ({
    ...r,
    positions: (r.stops || []).filter(s => s.lat && s.lng).map(s => [s.lat, s.lng]),
    col: color(r.id),
  }));

  return (
    <div className="h-full flex flex-col space-y-0 max-w-full" data-testid="route-planner">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3 pb-4">
        <div>
          <h1 className="text-3xl font-extrabold font-[Manrope] text-[#3A322C] flex items-center gap-3">
            <span className="w-10 h-10 bg-[#E8956D]/15 rounded-xl flex items-center justify-center"><Map size={20} className="text-[#E8956D]" /></span>
            Route Planner
          </h1>
          <p className="text-sm text-[#8A7E73] mt-0.5 ml-14">Design routes, assign schools, and track changes</p>
        </div>
        <div className="flex gap-2">
          {/* View as of date picker */}
          <div className="flex items-center gap-2 border border-[#E5E1DB] rounded-lg px-3 py-1.5 bg-white">
            <Clock size={13} className="text-[#8A7E73]" />
            <input type="date"
              value={asOfDate}
              max={new Date().toISOString().split("T")[0]}
              onChange={e => { setAsOfDate(e.target.value); loadAsOf(e.target.value); }}
              className="text-xs text-[#3A322C] outline-none bg-transparent"
              title="View route assignments as they existed on this date"
            />
            {asOfDate && (
              <button onClick={() => { setAsOfDate(""); setAsOfRoutes(null); }}
                className="text-[#C4BAAF] hover:text-[#C2624E]"><X size={12} /></button>
            )}
          </div>
          {asOfDate && (
            <div className="flex items-center gap-1.5 bg-amber-50 border border-amber-200 rounded-lg px-3 py-1.5 text-xs font-bold text-amber-700">
              {asOfLoading ? <Loader size={11} className="animate-spin" /> : <BarChart3 size={11} />}
              Viewing {asOfDate}
            </div>
          )}
          {canEdit && (
            <>
              <button onClick={runMigration} disabled={migrating}
                className="border border-[#637C64] text-[#637C64] rounded-lg px-3 py-2 text-sm font-bold flex items-center gap-2 hover:bg-[#EDF2EE] disabled:opacity-60"
                title="Populate route stops from existing school-route assignments in the school master">
                {migrating ? <Loader size={14} className="animate-spin" /> : <Link size={14} />}
                Sync Routes
              </button>
              <button onClick={() => setShowCreate(true)}
                className="bg-[#E8956D] text-[#3A322C] rounded-lg px-4 py-2 text-sm font-bold flex items-center gap-2 hover:bg-[#D48661]">
                <Plus size={15} /> New Route
              </button>
            </>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-[#E5E1DB] mb-4">
        {[
          { id: "planner", label: "Route Planner" },
          { id: "geocode", label: "School Geocoding" },
          { id: "history", label: "Change History" },
        ].map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
            className={`px-4 py-2.5 text-sm font-semibold -mb-px transition-colors ${tab === t.id ? "border-b-2 border-[#E8956D] text-[#E8956D]" : "text-[#8A7E73] hover:text-[#5C5147]"}`}>
            {t.label}
          </button>
        ))}
      </div>

      {msg && <div className="bg-[#EDF2EE] text-[#637C64] rounded-xl px-4 py-2.5 text-sm flex items-center gap-2 mb-3"><CheckCircle size={14}/>{msg}</div>}
      {error && <div className="bg-[#FBEAE7] text-[#C2624E] rounded-xl px-4 py-2.5 text-sm mb-3">{error}<button onClick={() => setError("")} className="ml-2 font-bold">×</button></div>}

      {/* ── PLANNER TAB ── */}
      {tab === "planner" && (
        <div className="flex gap-4 h-[calc(100vh-280px)] min-h-[600px]">
          {/* Left panel */}
          <div className="w-80 shrink-0 flex flex-col gap-3 overflow-y-auto">
            {/* Route list */}
            <div className="bg-white border border-[#E5E1DB] rounded-xl overflow-hidden">
              <div className="px-4 py-2.5 bg-[#F4F3F0] border-b border-[#E5E1DB] flex items-center justify-between">
                <span className="text-xs font-bold text-[#5C5147] uppercase">Routes ({routes.length})</span>
                {loading && <Loader size={13} className="animate-spin text-[#E8956D]" />}
              </div>
              <div className="divide-y divide-[#F4F3F0] max-h-48 overflow-y-auto">
                {routes.map(r => (
                  <div key={r.id}
                    className={`flex items-center gap-2 px-3 py-2 cursor-pointer hover:bg-[#FAFAF8] ${selectedRouteId === r.id ? "bg-[#E8956D]/5" : ""}`}
                    onClick={() => setSelectedRouteId(r.id)}
                  >
                    <span className="w-3 h-3 rounded-full shrink-0" style={{ background: color(r.id) }} />
                    <div className="flex-1 min-w-0">
                      <p className={`text-xs font-bold truncate ${selectedRouteId === r.id ? "text-[#E8956D]" : "text-[#3A322C]"}`}>
                        Route {r.route_number} {r.name ? `— ${r.name}` : ""}
                      </p>
                      <p className="text-[10px] text-[#8A7E73]">{r.stop_count} stops</p>
                    </div>
                    <button
                      onClick={e => { e.stopPropagation(); setVisibleRouteIds(prev => { const n = new Set(prev); n.has(r.id) ? n.delete(r.id) : n.add(r.id); return n; }); }}
                      className={`text-[#8A7E73] hover:text-[#E8956D] ${visibleRouteIds.has(r.id) ? "text-[#E8956D]" : ""}`} title="Toggle on map">
                      {visibleRouteIds.has(r.id) ? <Eye size={12} /> : <EyeOff size={12} />}
                    </button>
                    {canEdit && (
                      <button onClick={e => { e.stopPropagation(); retireRoute(r.id); }}
                        className="text-[#C4BAAF] hover:text-[#C2624E]" title="Retire route"><Trash2 size={12} /></button>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Selected route stops */}
            {selectedRoute && (
              <div className="bg-white border border-[#E5E1DB] rounded-xl overflow-hidden flex-1">
                <div className="px-4 py-2.5 bg-[#F4F3F0] border-b border-[#E5E1DB] flex items-center justify-between">
                  <div>
                    <span className="text-xs font-bold text-[#5C5147] uppercase">
                      Route {selectedRoute.route_number} — Stops
                    </span>
                    <span className="text-[10px] text-[#8A7E73] ml-2">{editingStops.length} schools</span>
                  </div>
                  {canEdit && stopsDirty.current && (
                    <button onClick={saveOrder}
                      className="text-[10px] bg-[#E8956D] text-[#3A322C] px-2 py-1 rounded font-bold">
                      Save Order
                    </button>
                  )}
                </div>

                {/* Add school */}
                {canEdit && (
                  <div className="p-3 border-b border-[#F4F3F0]">
                    <div className="relative">
                      <Search size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#8A7E73]" />
                      <input placeholder="Add school to route..." value={schoolSearch}
                        onChange={e => searchSchools(e.target.value)}
                        className="w-full border border-[#E5E1DB] rounded-lg pl-7 pr-3 py-1.5 text-xs outline-none focus:border-[#E8956D]" />
                    </div>
                    {schoolResults.length > 0 && (
                      <div className="mt-1 border border-[#E5E1DB] rounded-lg overflow-hidden max-h-32 overflow-y-auto">
                        {schoolResults.map(s => (
                          <button key={s.id} onClick={() => addSchool(s)}
                            className="w-full text-left px-3 py-1.5 text-xs hover:bg-[#F4F3F0] flex items-center gap-1">
                            <Plus size={10} className="text-[#E8956D]" />
                            <span className="font-medium text-[#3A322C] truncate">{s.school_name}</span>
                            <span className="text-[#C4BAAF] shrink-0">{s.mandal}</span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Stops list (sortable) */}
                <div className="p-2 space-y-1 overflow-y-auto max-h-64">
                  {editingStops.length === 0 ? (
                    <p className="text-xs text-[#8A7E73] text-center py-4">No stops yet — add schools above</p>
                  ) : (
                    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                      <SortableContext items={editingStops.map(s => s.school_id)} strategy={verticalListSortingStrategy}>
                        {editingStops.map(stop => (
                          <div key={stop.school_id} className="flex items-center gap-1">
                            <input type="checkbox" className="accent-[#E8956D]"
                              checked={selectedSchoolIds.has(stop.school_id)}
                              onChange={e => {
                                const n = new Set(selectedSchoolIds);
                                e.target.checked ? n.add(stop.school_id) : n.delete(stop.school_id);
                                setSelectedSchoolIds(n);
                              }} />
                            <div className="flex-1">
                              <SortableStop stop={stop} color={color(selectedRouteId)}
                                onRemove={canEdit ? removeSchool : () => {}}
                                onFocus={c => setMapCenter(c)} />
                            </div>
                          </div>
                        ))}
                      </SortableContext>
                    </DndContext>
                  )}
                </div>

                {/* Bulk move */}
                {canEdit && selectedSchoolIds.size > 0 && (
                  <div className="p-3 border-t border-[#F4F3F0] flex gap-2 items-center">
                    <span className="text-xs text-[#5C5147]">{selectedSchoolIds.size} selected</span>
                    <select value={moveToRouteId} onChange={e => setMoveToRouteId(e.target.value)}
                      className="flex-1 border border-[#E5E1DB] rounded px-2 py-1 text-xs outline-none focus:border-[#E8956D]">
                      <option value="">Move to route...</option>
                      {routes.filter(r => r.id !== selectedRouteId).map(r => (
                        <option key={r.id} value={r.id}>Route {r.route_number}</option>
                      ))}
                    </select>
                    <button onClick={moveSelectedSchools} disabled={!moveToRouteId}
                      className="text-xs bg-[#E8956D] text-[#3A322C] px-3 py-1 rounded font-bold disabled:opacity-50">
                      <ArrowRight size={12} />
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* Unassigned pool */}
            <div className="bg-white border border-[#E5E1DB] rounded-xl overflow-hidden">
              <div className="px-4 py-2.5 bg-[#FDF4EB] border-b border-[#D98032]/30 flex items-center justify-between">
                <span className="text-xs font-bold text-[#D98032] uppercase">Unassigned Schools ({unassigned.length})</span>
              </div>
              <div className="p-2">
                <div className="relative mb-2">
                  <Search size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#8A7E73]" />
                  <input placeholder="Filter unassigned..." value={unassignedSearch}
                    onChange={e => setUnassignedSearch(e.target.value)}
                    className="w-full border border-[#E5E1DB] rounded-lg pl-7 pr-3 py-1.5 text-xs outline-none focus:border-[#E8956D]" />
                </div>
                <div className="max-h-40 overflow-y-auto space-y-1">
                  {unassigned.slice(0, 30).map(s => (
                    <div key={s.id} className="flex items-center justify-between gap-2 px-2 py-1.5 rounded hover:bg-[#FAFAF8]">
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-medium text-[#3A322C] truncate">{s.school_name}</p>
                        <p className="text-[10px] text-[#8A7E73]">{s.mandal}</p>
                      </div>
                      {canEdit && selectedRouteId && (
                        <button onClick={() => addSchool(s)}
                          className="text-[10px] bg-[#E8956D]/10 text-[#E8956D] px-2 py-0.5 rounded font-bold hover:bg-[#E8956D]/20 shrink-0">
                          + Add
                        </button>
                      )}
                    </div>
                  ))}
                  {unassigned.length === 0 && <p className="text-xs text-[#8A7E73] text-center py-3">All schools assigned!</p>}
                </div>
              </div>
            </div>
          </div>

          {/* Right panel — Leaflet Map */}
          <div className="flex-1 rounded-2xl overflow-hidden border border-[#E5E1DB] relative">
            <MapContainer
              center={[17.385, 78.4867]}
              zoom={10}
              style={{ height: "100%", width: "100%" }}
              className="z-10"
            >
              <MapLayersControl defaultLayer="streets" />
              <LayerPreferenceSaver />
              {mapCenter && <FlyTo center={mapCenter} />}

              {/* Render each visible route */}
              {routePolylines.map(route => (
                <React.Fragment key={route.id}>
                  {route.positions.length >= 2 && (
                    <Polyline positions={route.positions} color={route.col} weight={3} opacity={0.7} />
                  )}
                  {(route.stops || []).filter(s => s.lat && s.lng).map(stop => (
                    <Marker key={stop.school_id} position={[stop.lat, stop.lng]}
                      icon={numberedIcon(stop.sequence, route.col)}>
                      <Popup>
                        <div className="text-sm">
                          <p className="font-bold">{stop.sequence}. {stop.school_name}</p>
                          <p className="text-gray-500 text-xs">{stop.mandal}</p>
                          <p className="text-xs text-gray-400 mt-1">Route {route.route_number}</p>
                          {canEdit && (
                            <div className="mt-2 flex gap-1 flex-wrap">
                              {routes.filter(r => r.id !== route.id).slice(0, 3).map(r => (
                                <button key={r.id}
                                  onClick={async () => {
                                    if (!window.confirm(`Move ${stop.school_name} to Route ${r.route_number}?`)) return;
                                    await routePlannerAPI.moveSchools({ school_ids: [stop.school_id], from_route_id: route.id, to_route_id: r.id });
                                    fetchRoutes();
                                  }}
                                  className="text-[10px] bg-blue-100 text-blue-700 px-2 py-0.5 rounded font-bold">
                                  → R{r.route_number}
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                      </Popup>
                    </Marker>
                  ))}
                </React.Fragment>
              ))}
            </MapContainer>

            {/* Map legend */}
            {visibleRouteIds.size > 0 && (
              <div className="absolute top-3 right-3 z-20 bg-white/90 backdrop-blur-sm rounded-xl border border-[#E5E1DB] p-3 max-w-[180px]">
                {mapRoutes.map(r => (
                  <div key={r.id} className="flex items-center gap-2 text-xs mb-1">
                    <span className="w-4 h-1.5 rounded-full shrink-0" style={{ background: color(r.id) }} />
                    <span className="font-medium truncate">R{r.route_number} ({r.stop_count})</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── GEOCODE TAB ── */}
      {tab === "geocode" && (
        <div className="space-y-5 max-w-4xl">
          {/* Status */}
          {geoStatus && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {[
                { label: "Total Schools", value: geoStatus.total, color: "text-[#3A322C]" },
                { label: "With Coordinates", value: geoStatus.with_coords, color: "text-[#637C64]" },
                { label: "Auto-geocoded", value: geoStatus.auto_geocoded, color: "text-blue-600" },
                { label: "Failed", value: geoStatus.geocode_failed, color: "text-[#C2624E]" },
              ].map(s => (
                <div key={s.label} className="bg-white border border-[#E5E1DB] rounded-xl p-4">
                  <p className="text-xs text-[#8A7E73] uppercase font-bold mb-1">{s.label}</p>
                  <p className={`text-2xl font-extrabold ${s.color}`}>{s.value}</p>
                </div>
              ))}
            </div>
          )}

          <div className="flex gap-3 flex-wrap">
            {canEdit && (
              <button onClick={startGeocodeAll} disabled={geoLoading}
                className="bg-[#637C64] text-white rounded-lg px-4 py-2.5 text-sm font-bold flex items-center gap-2 hover:bg-[#4F6450] disabled:opacity-60">
                {geoLoading ? <Loader size={14} className="animate-spin" /> : <Navigation size={14} />}
                Geocode All Schools (Nominatim)
              </button>
            )}
            <button onClick={loadGeoStatus}
              className="border border-[#E5E1DB] rounded-lg px-4 py-2.5 text-sm font-bold text-[#5C5147] hover:bg-[#F4F3F0] flex items-center gap-2">
              <RefreshCw size={14} /> Refresh Status
            </button>
          </div>

          <div className="bg-[#F4F3F0] rounded-xl px-4 py-3 text-xs text-[#8A7E73]">
            <strong className="text-[#5C5147]">Geocoding runs in the background at 1 req/sec</strong> (Nominatim policy). 
            For failures, use "Set Location" on the School Master page to pin-drop or capture GPS coordinates from the field.
          </div>

          {/* Schools needing attention */}
          <SchoolGeoList canEdit={canEdit} onCapture={captureLocation} />
        </div>
      )}

      {/* ── HISTORY TAB ── */}
      {tab === "history" && (
        <div className="space-y-4 max-w-5xl">
          <div className="flex justify-end">
            <button onClick={loadChangelog}
              className="border border-[#E5E1DB] rounded-lg px-3 py-2 text-sm font-bold text-[#5C5147] hover:bg-[#F4F3F0] flex items-center gap-1.5">
              <RefreshCw size={13} /> Refresh
            </button>
          </div>
          <div className="bg-white border border-[#E5E1DB] rounded-xl overflow-hidden">
            {changelog.length === 0 ? (
              <div className="text-center py-12 text-[#8A7E73] text-sm">No changes recorded yet</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-[#F4F3F0]">
                    <tr>{["Timestamp","Action","Route","Impact","By"].map(h => (
                      <th key={h} className="text-left px-4 py-3 text-xs font-bold uppercase text-[#5C5147]">{h}</th>
                    ))}</tr>
                  </thead>
                  <tbody className="divide-y divide-[#F4F3F0]">
                    {changelog.map((l, i) => (
                      <tr key={`item-${i}`} className="hover:bg-[#FAFAF8]">
                        <td className="px-4 py-3 text-xs text-[#8A7E73] whitespace-nowrap">{fmtDateTime(l.timestamp)}</td>
                        <td className="px-4 py-3">
                          <span className="text-xs bg-[#E8956D]/15 text-[#E8956D] px-2 py-0.5 rounded-full font-bold capitalize">
                            {l.action?.replace(/_/g," ")}
                          </span>
                        </td>
                        <td className="px-4 py-3 font-bold text-[#E8956D] text-xs">R{l.route_number}</td>
                        <td className="px-4 py-3 text-xs text-[#5C5147] max-w-[250px] truncate">{l.impact || "—"}</td>
                        <td className="px-4 py-3 text-xs text-[#8A7E73]">{l.user_name}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── CREATE ROUTE MODAL ── */}
      {showCreate && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md animate-fade-in-up">
            <div className="flex items-center justify-between px-6 py-4 border-b border-[#E5E1DB]">
              <h2 className="font-bold text-[#3A322C] font-[Manrope]">Create New Route</h2>
              <button onClick={() => setShowCreate(false)}><X size={20} /></button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Route Number *</label>
                <input type="text" placeholder="e.g. 119" value={createForm.route_number}
                  onChange={e => setCreateForm(f => ({ ...f, route_number: e.target.value }))}
                  className="w-full border border-[#E5E1DB] rounded-xl px-4 py-3 text-sm outline-none focus:border-[#E8956D]" />
              </div>
              <div>
                <label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Route Name (optional)</label>
                <input type="text" placeholder="e.g. North Hyderabad" value={createForm.name}
                  onChange={e => setCreateForm(f => ({ ...f, name: e.target.value }))}
                  className="w-full border border-[#E5E1DB] rounded-xl px-4 py-3 text-sm outline-none focus:border-[#E8956D]" />
              </div>
              <div className="flex gap-3">
                <button onClick={() => setShowCreate(false)} className="flex-1 border border-[#E5E1DB] rounded-lg py-2.5 text-sm font-bold text-[#5C5147]">Cancel</button>
                <button onClick={handleCreate} disabled={!createForm.route_number.trim()}
                  className="flex-1 bg-[#E8956D] text-[#3A322C] rounded-lg py-2.5 text-sm font-bold disabled:opacity-50">
                  Create Route
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── School Geo List Component ────────────────────────────────────────────────
// ─── School Geo List — with manual coordinate entry, GPS, pin-drop ────────────
function SchoolGeoList({ canEdit, onCapture }) {
  const [schools, setSchools] = useState([]);
  const [filter, setFilter] = useState("");
  const [search, setSearch] = useState("");
  const [pinDrop, setPinDrop] = useState(null);
  const [manualEntry, setManualEntry] = useState(null); // school for manual input
  const [localMsg, setLocalMsg] = useState("");
  const [localErr, setLocalErr] = useState("");

  const load = async () => {
    try {
      const r = await routePlannerAPI.getSchools({ location_status: filter || undefined, search: search || undefined, limit: 50 });
      setSchools(r.data?.schools || []);
    } catch (e) { if (process.env.NODE_ENV === 'development') console.error('Fetch error:', e); }
  };
  useEffect(() => { load(); }, [filter, search]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="bg-white border border-[#E5E1DB] rounded-xl overflow-hidden">
      <div className="px-4 py-3 border-b border-[#F4F3F0] flex gap-3 flex-wrap items-center">
        <select value={filter} onChange={e => setFilter(e.target.value)}
          className="border border-[#E5E1DB] rounded-lg px-3 py-1.5 text-sm outline-none focus:border-[#E8956D]">
          <option value="">All Schools</option>
          <option value="geocode_failed">Geocode Failed</option>
          <option value="unverified">Unverified (no coords)</option>
          <option value="auto_geocoded">Auto-geocoded</option>
          <option value="verified">Verified</option>
        </select>
        <div className="relative flex-1 min-w-40">
          <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#8A7E73]" />
          <input placeholder="Search schools..." value={search} onChange={e => setSearch(e.target.value)}
            className="w-full border border-[#E5E1DB] rounded-lg pl-8 pr-3 py-1.5 text-sm outline-none focus:border-[#E8956D]" />
        </div>
      </div>

      {localMsg && <div className="px-4 py-2 text-xs text-[#637C64] bg-[#EDF2EE]">{localMsg}</div>}
      {localErr && <div className="px-4 py-2 text-xs text-[#C2624E] bg-[#FBEAE7]">{localErr}</div>}

      <div className="divide-y divide-[#F4F3F0] max-h-96 overflow-y-auto">
        {schools.slice(0, 50).map(s => (
          <div key={s.id} className="flex items-center gap-3 px-4 py-3 hover:bg-[#FAFAF8] flex-wrap">
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-[#3A322C] truncate">{s.school_name}</p>
              <p className="text-xs text-[#8A7E73]">{s.mandal}, {s.district}</p>
              {s.lat && <p className="text-[10px] text-[#637C64] font-mono">{s.lat?.toFixed(5)}, {s.lng?.toFixed(5)}</p>}
            </div>
            <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full shrink-0 ${
              s.location_status === "verified" ? "bg-[#EDF2EE] text-[#637C64]" :
              s.location_status === "auto_geocoded" ? "bg-blue-100 text-blue-700" :
              s.location_status === "geocode_failed" ? "bg-[#FBEAE7] text-[#C2624E]" :
              "bg-[#F4F3F0] text-[#8A7E73]"
            }`}>{s.location_status || "unverified"}</span>
            {canEdit && (
              <div className="flex gap-1 flex-wrap">
                {/* Manual coordinate entry */}
                <button onClick={() => setManualEntry(s)}
                  className="text-xs bg-purple-50 text-purple-700 border border-purple-200 px-2 py-1 rounded font-bold hover:bg-purple-100 flex items-center gap-1"
                  title="Enter coordinates manually (type lat/lng or search by name)">
                  <Type size={10} /> Manual
                </button>
                {/* GPS capture */}
                <button onClick={() => {
                  onCapture(s.id);
                  setLocalMsg(`Capturing GPS for ${s.school_name}...`);
                  setTimeout(() => { setLocalMsg(""); load(); }, 3000);
                }}
                  className="text-xs bg-[#E8956D]/10 text-[#E8956D] border border-[#E8956D]/30 px-2 py-1 rounded font-bold hover:bg-[#E8956D]/20 flex items-center gap-1"
                  title="Capture current GPS location (requires browser permission)">
                  <Crosshair size={10} /> GPS
                </button>
                {/* Pin-drop on map */}
                <button onClick={() => setPinDrop(s)}
                  className="text-xs bg-[#637C64]/10 text-[#637C64] border border-[#637C64]/30 px-2 py-1 rounded font-bold hover:bg-[#637C64]/20 flex items-center gap-1"
                  title="Drop a pin on the map">
                  <MapPin size={10} /> Pin
                </button>
              </div>
            )}
          </div>
        ))}
        {schools.length === 0 && <p className="text-center py-8 text-[#8A7E73] text-sm">No schools matching filter</p>}
      </div>

      {/* Manual coordinate entry modal */}
      {manualEntry && (
        <ManualCoordModal
          school={manualEntry}
          onClose={() => { setManualEntry(null); load(); }}
          onSaved={(name) => { setLocalMsg(`✓ Coordinates saved for ${name}`); setTimeout(() => setLocalMsg(""), 3000); }}
        />
      )}

      {/* Pin-drop modal */}
      {pinDrop && <PinDropModal school={pinDrop} onClose={() => { setPinDrop(null); load(); }} />}
    </div>
  );
}

// ─── Manual Coordinate Entry Modal ───────────────────────────────────────────
function ManualCoordModal({ school, onClose, onSaved }) {
  const [lat, setLat] = useState(school.lat ? String(school.lat) : "");
  const [lng, setLng] = useState(school.lng ? String(school.lng) : "");
  const [nmSearch, setNmSearch] = useState(`${school.school_name}, ${school.mandal}, ${school.district}, Telangana`);
  const [nmResults, setNmResults] = useState([]);
  const [nmLoading, setNmLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  const searchNominatim = async () => {
    if (!nmSearch.trim()) return;
    setNmLoading(true); setNmResults([]);
    try {
      const r = await routePlannerAPI.searchNominatim(nmSearch);
      setNmResults(r.data || []);
      if (!r.data?.length) setErr("No results found. Try a shorter name or enter coordinates manually.");
    } catch (e) { setErr("Search failed. Enter coordinates manually."); }
    setNmLoading(false);
  };

  const pickResult = (r) => {
    setLat(String(r.lat.toFixed(6)));
    setLng(String(r.lng.toFixed(6)));
    setNmResults([]);
  };

  const save = async () => {
    const latN = parseFloat(lat), lngN = parseFloat(lng);
    if (isNaN(latN) || isNaN(lngN)) { setErr("Enter valid numbers for latitude and longitude"); return; }
    if (latN < 5 || latN > 40 || lngN < 68 || lngN > 98) {
      setErr("Coordinates seem outside India. Check the values (lat: 5–40, lng: 68–98).");
      return;
    }
    setSaving(true);
    try {
      await routePlannerAPI.setSchoolLocation(school.id, { lat: latN, lng: lngN, location_status: "verified" });
      onSaved(school.school_name);
      onClose();
    } catch (e) { setErr("Save failed: " + e.message); }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg animate-fade-in-up">
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#E5E1DB]">
          <div>
            <h2 className="font-bold text-[#3A322C] font-[Manrope]">Set Coordinates</h2>
            <p className="text-xs text-[#8A7E73] mt-0.5 truncate max-w-xs">{school.school_name}</p>
          </div>
          <button onClick={onClose}><X size={20} /></button>
        </div>
        <div className="p-6 space-y-5">
          {/* Option 1: Search by name */}
          <div>
            <label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Search by Name (Nominatim / OpenStreetMap)</label>
            <div className="flex gap-2">
              <input value={nmSearch} onChange={e => setNmSearch(e.target.value)}
                onKeyDown={e => e.key === "Enter" && searchNominatim()}
                className="flex-1 border border-[#E5E1DB] rounded-lg px-3 py-2 text-sm outline-none focus:border-[#E8956D]"
                placeholder="School name, mandal, district..." />
              <button onClick={searchNominatim} disabled={nmLoading}
                className="bg-[#637C64] text-white rounded-lg px-4 py-2 text-sm font-bold disabled:opacity-60 flex items-center gap-1">
                {nmLoading ? <Loader size={13} className="animate-spin" /> : <Search size={13} />}
              </button>
            </div>
            {nmResults.length > 0 && (
              <div className="mt-2 border border-[#E5E1DB] rounded-xl overflow-hidden">
                {nmResults.map((r, i) => (
                  <button key={`idx-${i}`} onClick={() => pickResult(r)}
                    className="w-full text-left px-4 py-2.5 text-xs hover:bg-[#F4F3F0] border-b border-[#F4F3F0] last:border-b-0">
                    <p className="font-bold text-[#3A322C] text-[11px]">{r.display_name.slice(0, 80)}...</p>
                    <p className="text-[#8A7E73] font-mono mt-0.5">{r.lat.toFixed(5)}, {r.lng.toFixed(5)}</p>
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="relative flex items-center gap-3">
            <div className="flex-1 h-px bg-[#E5E1DB]" />
            <span className="text-xs text-[#C4BAAF] font-bold">OR ENTER MANUALLY</span>
            <div className="flex-1 h-px bg-[#E5E1DB]" />
          </div>

          {/* Option 2: Manual lat/lng input */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Latitude *</label>
              <input type="number" step="any" value={lat} onChange={e => setLat(e.target.value)}
                placeholder="e.g. 17.3850"
                className="w-full border border-[#E5E1DB] rounded-xl px-4 py-3 text-sm font-mono outline-none focus:border-[#E8956D]" />
            </div>
            <div>
              <label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Longitude *</label>
              <input type="number" step="any" value={lng} onChange={e => setLng(e.target.value)}
                placeholder="e.g. 78.4867"
                className="w-full border border-[#E5E1DB] rounded-xl px-4 py-3 text-sm font-mono outline-none focus:border-[#E8956D]" />
            </div>
          </div>

          <div className="bg-[#F4F3F0] rounded-xl px-4 py-3 text-xs text-[#8A7E73]">
            <strong className="text-[#5C5147]">How to get coordinates:</strong> Open Google Maps → long-press the school location → copy the numbers that appear at the top (e.g. <span className="font-mono">17.38500, 78.48670</span>)
          </div>

          {err && <p className="text-sm text-[#C2624E] bg-[#FBEAE7] px-3 py-2 rounded-lg">{err}</p>}

          <div className="flex gap-3">
            <button onClick={onClose} className="flex-1 border border-[#E5E1DB] rounded-lg py-2.5 text-sm font-bold text-[#5C5147]">Cancel</button>
            <button onClick={save} disabled={saving || !lat || !lng}
              className="flex-1 bg-[#637C64] text-white rounded-lg py-2.5 text-sm font-bold disabled:opacity-50">
              {saving ? "Saving..." : "Save Coordinates"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── PIN DROP MODAL — draggable marker + satellite/hybrid + coord inputs + undo ─

function PinDropModal({ school, onClose }) {
  const savedPos = school.lat ? [school.lat, school.lng] : [17.385, 78.4867];
  const [pos, setPos] = useState(savedPos);
  const [latInput, setLatInput] = useState(savedPos[0].toFixed(6));
  const [lngInput, setLngInput] = useState(savedPos[1].toFixed(6));
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const markerRef = useRef(null);

  // Keep text inputs in sync when map updates pos
  const updatePos = (lat, lng) => {
    const newPos = [lat, lng];
    setPos(newPos);
    setLatInput(lat.toFixed(6));
    setLngInput(lng.toFixed(6));
  };

  // Manual text input → move pin
  const applyManualCoords = () => {
    const lat = parseFloat(latInput), lng = parseFloat(lngInput);
    if (isNaN(lat) || isNaN(lng)) { setErr("Invalid coordinates"); return; }
    if (lat < 5 || lat > 40 || lng < 68 || lng > 98) { setErr("Coordinates outside India (lat 5–40, lng 68–98)"); return; }
    setErr("");
    setPos([lat, lng]);
    // Move the map view to the new position
    if (markerRef.current) {
      markerRef.current.setLatLng([lat, lng]);
    }
  };

  // Undo: revert to saved position
  const undo = () => updatePos(savedPos[0], savedPos[1]);

  const save = async () => {
    setSaving(true);
    setErr("");
    try {
      await routePlannerAPI.setSchoolLocation(school.id, {
        lat: pos[0], lng: pos[1], location_status: "verified"
      });
      onClose();
    } catch (e) {
      setErr("Save failed: " + e.message);
      if (process.env.NODE_ENV === 'development') console.error('Save error:', e);
    }
    setSaving(false);
  };

  const hasChanged = Math.abs(pos[0] - savedPos[0]) > 0.000001 || Math.abs(pos[1] - savedPos[1]) > 0.000001;

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl animate-fade-in-up">
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#E5E1DB]">
          <div>
            <h2 className="font-bold text-[#3A322C]">Set School Location</h2>
            <p className="text-xs text-[#8A7E73] truncate max-w-sm">{school.school_name} — drag pin, click map, or type coordinates</p>
          </div>
          <button onClick={onClose}><X size={20} /></button>
        </div>

        {/* Map */}
        <div style={{ height: 380 }} className="relative">
          <MapContainer
            center={pos} zoom={15}
            style={{ height: "100%", width: "100%" }}
            className="z-10"
          >
            <MapLayersControl defaultLayer="hybrid" />
            <LayerPreferenceSaver />
            <ClickAndDragMap onPositionChange={updatePos} />
            <Marker
              position={pos}
              draggable={true}
              ref={markerRef}
              eventHandlers={{
                dragend(e) {
                  const { lat, lng } = e.target.getLatLng();
                  updatePos(lat, lng);
                },
              }}
            />
            <FlyToOnChange pos={pos} />
          </MapContainer>
        </div>

        {/* Coordinate inputs + undo */}
        <div className="px-6 py-4 border-t border-[#F4F3F0] space-y-3">
          <div className="flex gap-3 items-end">
            <div className="flex-1">
              <label className="text-[10px] font-bold uppercase text-[#8A7E73] mb-0.5 block">Latitude</label>
              <input
                type="text" value={latInput}
                onChange={e => setLatInput(e.target.value)}
                onBlur={applyManualCoords}
                onKeyDown={e => e.key === "Enter" && applyManualCoords()}
                className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2 text-sm font-mono outline-none focus:border-[#E8956D]"
                placeholder="e.g. 17.385044"
              />
            </div>
            <div className="flex-1">
              <label className="text-[10px] font-bold uppercase text-[#8A7E73] mb-0.5 block">Longitude</label>
              <input
                type="text" value={lngInput}
                onChange={e => setLngInput(e.target.value)}
                onBlur={applyManualCoords}
                onKeyDown={e => e.key === "Enter" && applyManualCoords()}
                className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2 text-sm font-mono outline-none focus:border-[#E8956D]"
                placeholder="e.g. 78.486671"
              />
            </div>
            <button onClick={applyManualCoords}
              className="border border-[#E8956D] text-[#E8956D] rounded-lg px-3 py-2 text-sm font-bold hover:bg-[#E8956D]/10">
              Apply
            </button>
          </div>
          {err && <p className="text-xs text-[#C2624E]">{err}</p>}
          <div className="flex gap-3 justify-between">
            <div className="flex gap-2">
              <button onClick={onClose}
                className="border border-[#E5E1DB] rounded-lg px-4 py-2 text-sm font-bold text-[#5C5147]">
                Cancel
              </button>
              {hasChanged && (
                <button onClick={undo}
                  className="border border-[#D98032]/40 text-[#D98032] rounded-lg px-3 py-2 text-sm font-bold hover:bg-[#FDF4EB] flex items-center gap-1"
                  title="Undo — revert to last saved position">
                  <Undo2 size={13} /> Undo
                </button>
              )}
            </div>
            <button onClick={save} disabled={saving}
              className="bg-[#637C64] text-white rounded-lg px-5 py-2 text-sm font-bold disabled:opacity-60 flex items-center gap-2">
              {saving ? <Loader size={13} className="animate-spin" /> : <CheckCircle size={13} />}
              {saving ? "Saving..." : "Save Location"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/** Moves the map view smoothly when pos changes programmatically (e.g. from coord inputs). */
function FlyToOnChange({ pos }) {
  const map = useMap();
  const prevPos = useRef(pos);
  useEffect(() => {
    if (prevPos.current[0] !== pos[0] || prevPos.current[1] !== pos[1]) {
      map.setView(pos, map.getZoom(), { animate: true, duration: 0.5 });
      prevPos.current = pos;
    }
  }, [pos, map]);
  return null;
}


